import { Params } from 'app/types';
import { useRef } from 'react';
import { Controller, useController } from 'react-hook-form';

import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Input, InputLabel, ListItemText, ListSubheader, MenuItem, Select, SelectChangeEvent } from '@mui/material';

export interface BasicMultiSelectProps {
  collection: Params[];
  label: string;
  control: any;
  inputName: string;
  helperText?: string;
  onChange?: (event: SelectChangeEvent<any>) => void;
  isNested?: boolean;
}

export default function BasicMultiSelect(props: BasicMultiSelectProps) {
  const { collection, control, label, inputName, helperText, onChange, isNested = false } = props;

  const {
    field: { onChange: fieldOnChange },
  } = useController({
    name: inputName,
    control,
    defaultValue: [],
  });

  // Trigger whole form onchange
  const myRef = useRef<HTMLInputElement>();

  const getItemNameById = (id: string): string => {
    return collection.find((item: Params) => item.id === id)?.name || '';
  };

  const renderValues = (selected: Array<string>): string => {
    if (selected.length === 0) {
      return '- Select -';
    }
    return selected
      .map((itemId) => {
        return getItemNameById(itemId);
      })
      .join(', ');
  };

  const isWithGroups = collection.some((item) => item.id === 'optgroup');

  const handleChangeHeader = (id: string, fieldValue: string[]) => {
    if (isParentChecked(id, fieldValue)) {
      // remove all children
      const newArray = fieldValue.filter((item) => {
        const childItems = collection.filter((colItem) => colItem.parentId === id).map((colItem) => colItem.id);
        return !childItems.includes(item);
      });
      fieldOnChange(newArray);
      return;
    }

    // add all children
    const childrenToAdd = collection.filter((colItem) => colItem.parentId === id).map((colItem) => colItem.id);
    const newArray = Array.from(new Set([...fieldValue, ...childrenToAdd]));
    fieldOnChange(newArray);
    return;
  };

  const isIndeterminate = (id: string, fieldValue: string[]): boolean => {
    const checked = collection.filter((item) => fieldValue.includes(item.id));
    const checkedChildren = checked.filter((item) => item.parentId === id);
    const allChildren = collection.filter((item) => item.parentId === id);
    return allChildren.length > 0 && allChildren.length > checkedChildren.length && checkedChildren.length > 0;
  };

  const isParentChecked = (id: string, fieldValue: string[]): boolean => {
    const checked = collection.filter((item) => fieldValue.includes(item.id));
    const checkedChildren = checked.filter((item) => item.parentId === id);
    const allChildren = collection.filter((item) => item.parentId === id);
    return allChildren.length > 0 && allChildren.length === checkedChildren.length;
  };

  return (
    <Controller
      name={inputName}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl error={!!fieldState.error}>
          <InputLabel shrink htmlFor={inputName}>
            {label}
          </InputLabel>
          <Select
            {...field}
            displayEmpty
            error={!!fieldState.error}
            input={<Input ref={myRef} />}
            multiple
            renderValue={(selected: Array<string> | unknown) => {
              return renderValues(selected as Array<string>);
            }}
            onChange={(e) => {
              field.onChange(e);
              onChange && onChange(e);

              if (myRef.current !== null) {
                myRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }}
          >
            <MenuItem disabled value="" className="empty">
              - Select -
            </MenuItem>

            <Box component="li" sx={{ fontWeight: 'bold', lineHeight: '1.38' }}></Box>
            {collection.map((item: Params, index: number) => {
              if (item.id === 'optgroup') {
                return (
                  <ListSubheader
                    sx={{ pt: 2, pb: isNested ? 1 : 0, lineHeight: '1.38', zIndex: 20 }}
                    key={index}
                    component="li"
                    role="presentation"
                  >
                    {isNested && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isParentChecked(item.idAsParent, field.value)}
                            indeterminate={isIndeterminate(item.idAsParent, field.value)}
                            onChange={() => handleChangeHeader(item.idAsParent, field.value)}
                          />
                        }
                        label={
                          <Box fontWeight="bold" fontSize="1.4rem">
                            {item.name}
                          </Box>
                        }
                      />
                    )}
                    {!isNested && <>{item.name}</>}
                  </ListSubheader>
                );
              } else {
                return (
                  <MenuItem
                    key={`${index}-${item.id}`}
                    value={item.id}
                    sx={isWithGroups && item.parentId ? { ml: 2 } : { ml: -1.2 }}
                  >
                    <Checkbox checked={field.value.indexOf(item.id) > -1} />
                    <ListItemText primary={item.name} />
                  </MenuItem>
                );
              }
            })}
          </Select>
          {(!!fieldState.error || helperText) && (
            <FormHelperText>{fieldState.error?.message || helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
